import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Loader2,
  Save,
  Sparkles,
  AlertCircle,
  GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ApiPreviewPanel } from "@/components/ApiPreviewPanel";
import { toast } from "sonner";
import {
  usePublishEvalPack,
  useMyEvalPack,
} from "@/hooks/use-queries";
import { QUIZ_VISUALS, QuizVisualHeader } from "@/components/quiz/QuizVisualHeader";

type ItemType =
  | "MCQ"
  | "SHORT_ANSWER"
  | "STRUCTURED_TASK"
  | "CODE_TASK"
  | "API_TASK"
  | "SQL_TASK";

const ITEM_TYPES: { value: ItemType; label: string; help: string }[] = [
  { value: "MCQ", label: "Multiple choice", help: "Graded by exact option match." },
  { value: "SHORT_ANSWER", label: "Short answer", help: "Graded case-insensitively; any golden keyword counts." },
  { value: "STRUCTURED_TASK", label: "Structured JSON", help: "Graded by deep JSON equality. Keep it tiny + deterministic." },
  { value: "CODE_TASK", label: "Code task", help: "Expected answer matched as JSON / keywords." },
  { value: "API_TASK", label: "API task", help: "Expected answer matched as JSON / keywords." },
  { value: "SQL_TASK", label: "SQL task", help: "Expected answer matched as JSON / keywords." },
];

interface ItemDraft {
  itemId: string;
  type: ItemType;
  section: string;
  prompt: string;
  points: number;
  timeBudgetSec: number;
  // MCQ
  choices: { id: string; text: string }[];
  correctOptionId: string;
  // SHORT_ANSWER
  goldenKeywords: string;
  correctValue: string;
  // STRUCTURED_TASK / CODE / API / SQL
  correctJson: string;
}

let uid = 0;
const nextItemId = () => `item_${Date.now().toString(36)}_${uid++}`;

function blankItem(order: number): ItemDraft {
  return {
    itemId: `q${order + 1}_${nextItemId()}`,
    type: "MCQ",
    section: "General",
    prompt: "",
    points: 1,
    timeBudgetSec: 60,
    choices: [
      { id: "a", text: "" },
      { id: "b", text: "" },
    ],
    correctOptionId: "a",
    goldenKeywords: "",
    correctValue: "",
    correctJson: "{\n  \n}",
  };
}

// Serialize a single draft item into the API item shape (lenient — used for the
// live preview, so it never throws on incomplete input).
function serializeItem(it: ItemDraft, i: number) {
  const base = {
    itemId: it.itemId,
    type: it.type,
    section: it.section || "General",
    prompt: it.prompt,
    points: it.points || 1,
    timeBudgetSec: it.timeBudgetSec || 60,
    order: i,
  };
  if (it.type === "MCQ") {
    return { ...base, options: { choices: it.choices }, correct: { optionId: it.correctOptionId } };
  }
  if (it.type === "SHORT_ANSWER") {
    const keywords = it.goldenKeywords.split(",").map((k) => k.trim()).filter(Boolean);
    return {
      ...base,
      goldenKeywords: keywords,
      ...(it.correctValue.trim() && { correct: { value: it.correctValue.trim() } }),
    };
  }
  let parsed: unknown = null;
  try { parsed = JSON.parse(it.correctJson); } catch { /* incomplete JSON while typing */ }
  return { ...base, correct: parsed };
}

export default function EvalBuilder() {
  const navigate = useNavigate();
  const { packId: editPackId } = useParams();
  const isEdit = !!editPackId;

  const { data: existing, isLoading: loadingExisting } = useMyEvalPack(editPackId);
  const publish = usePublishEvalPack();

  const [packId, setPackId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [passThreshold, setPassThreshold] = useState(70);
  const [modeDefault, setModeDefault] = useState("CLOSED_BOOK");
  const [isFree, setIsFree] = useState(true);
  const [priceUsdc, setPriceUsdc] = useState(0);
  const [certValidityDays, setCertValidityDays] = useState<number | "">("");
  const [items, setItems] = useState<ItemDraft[]>([blankItem(0)]);

  // Which top-level JSON key is being edited (flashes in the live preview).
  const [activeKeys, setActiveKeys] = useState<string[]>(["packId"]);
  const touch = (k: string) => setActiveKeys([k]);

  // Hydrate from existing pack on edit
  useEffect(() => {
    if (!existing) return;
    setPackId(existing.packId);
    setTitle(existing.title ?? "");
    setDescription(existing.description ?? "");
    setPassThreshold(existing.passThreshold ?? 70);
    setModeDefault(existing.modeDefault ?? "CLOSED_BOOK");
    setIsFree(existing.isFree ?? true);
    setPriceUsdc(Number(existing.priceUsdc ?? 0));
    setCertValidityDays(existing.certValidityDays ?? "");
    if (Array.isArray(existing.items) && existing.items.length) {
      setItems(
        existing.items.map((it: any, i: number): ItemDraft => {
          const choices = (it.options?.choices as { id: string; text: string }[]) ?? [
            { id: "a", text: "" },
            { id: "b", text: "" },
          ];
          return {
            itemId: it.itemId ?? `q${i + 1}_${nextItemId()}`,
            type: it.type,
            section: it.section ?? "General",
            prompt: it.prompt ?? "",
            points: it.points ?? 1,
            timeBudgetSec: it.timeBudgetSec ?? 60,
            choices,
            correctOptionId: it.correct?.optionId ?? choices[0]?.id ?? "a",
            goldenKeywords: Array.isArray(it.goldenKeywords) ? it.goldenKeywords.join(", ") : "",
            correctValue: typeof it.correct?.value === "string" ? it.correct.value : "",
            correctJson:
              it.type !== "MCQ" && it.type !== "SHORT_ANSWER" && it.correct
                ? JSON.stringify(it.correct, null, 2)
                : "{\n  \n}",
          };
        }),
      );
    }
  }, [existing]);

  const slugValid = /^[a-z0-9][a-z0-9_-]{2,60}$/.test(packId);
  const itemCountValid = items.length >= 5 && items.length <= 60;

  function patchItem(idx: number, patch: Partial<ItemDraft>) {
    touch("items");
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function addItem() {
    touch("items");
    setItems((prev) => [...prev, blankItem(prev.length)]);
  }
  function removeItem(idx: number) {
    touch("items");
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }
  function moveItem(idx: number, dir: -1 | 1) {
    touch("items");
    setItems((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }
  function addChoice(idx: number) {
    touch("items");
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const nextLetter = String.fromCharCode(97 + it.choices.length); // a, b, c...
        return { ...it, choices: [...it.choices, { id: nextLetter, text: "" }] };
      }),
    );
  }
  function removeChoice(idx: number, cid: string) {
    touch("items");
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== idx) return it;
        const choices = it.choices.filter((c) => c.id !== cid);
        return {
          ...it,
          choices,
          correctOptionId: it.correctOptionId === cid ? choices[0]?.id ?? "" : it.correctOptionId,
        };
      }),
    );
  }

  // Lenient serialization for the live JSON preview (never throws).
  const previewPack = useMemo(() => {
    return {
      packId: packId || "your-eval-id",
      title: title || "Untitled eval",
      ...(description.trim() && { description: description.trim() }),
      passThreshold,
      modeDefault,
      isFree,
      ...(!isFree && { priceUsdc: Number(priceUsdc) || 0 }),
      ...(certValidityDays !== "" && { certValidityDays: Number(certValidityDays) }),
      items: items.map(serializeItem),
    };
  }, [packId, title, description, passThreshold, modeDefault, isFree, priceUsdc, certValidityDays, items]);

  // Strict validation + serialize for the actual publish.
  function buildPayload(): Record<string, unknown> | null {
    if (!slugValid) {
      toast.error("Pack ID must be a lowercase slug (a-z, 0-9, _ or -, 3-61 chars).");
      return null;
    }
    if (!title.trim()) {
      toast.error("Give your eval a title.");
      return null;
    }
    if (!itemCountValid) {
      toast.error("A pack needs between 5 and 60 items.");
      return null;
    }
    const apiItems: any[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.prompt.trim()) {
        toast.error(`Item ${i + 1}: prompt is required.`);
        return null;
      }
      if (it.type === "MCQ") {
        const choices = it.choices.filter((c) => c.text.trim());
        if (choices.length < 2) {
          toast.error(`Item ${i + 1}: add at least 2 answer choices.`);
          return null;
        }
        if (!choices.some((c) => c.id === it.correctOptionId)) {
          toast.error(`Item ${i + 1}: mark which choice is correct.`);
          return null;
        }
      } else if (it.type === "SHORT_ANSWER") {
        const keywords = it.goldenKeywords.split(",").map((k) => k.trim()).filter(Boolean);
        if (keywords.length === 0 && !it.correctValue.trim()) {
          toast.error(`Item ${i + 1}: add golden keywords or an exact answer.`);
          return null;
        }
      } else {
        try {
          JSON.parse(it.correctJson);
        } catch {
          toast.error(`Item ${i + 1}: expected answer must be valid JSON.`);
          return null;
        }
      }
      apiItems.push(serializeItem(it, i));
    }

    return {
      packId,
      title: title.trim(),
      description: description.trim() || undefined,
      passThreshold,
      modeDefault,
      isFree,
      priceUsdc: isFree ? 0 : Number(priceUsdc) || 0,
      ...(certValidityDays !== "" && { certValidityDays: Number(certValidityDays) }),
      items: apiItems,
    };
  }

  async function handleSubmit() {
    const payload = buildPayload();
    if (!payload) return;
    try {
      await publish.mutateAsync(payload);
      toast.success(
        isEdit
          ? "Eval updated — back in the review queue."
          : "Eval published — it's now in the moderation queue.",
      );
      navigate("/dashboard/quiz/mine");
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to publish eval");
    }
  }

  const totalPoints = useMemo(
    () => items.reduce((s, it) => s + (it.points || 0), 0),
    [items],
  );

  if (isEdit && loadingExisting) {
    return (
      <div className="flex items-center justify-center py-32 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(380px,42%)] lg:min-h-[calc(100vh-9rem)]">
      {/* ============ LEFT — Builder form ============ */}
      <div className="space-y-6 pb-24 min-w-0">
        <QuizVisualHeader
          eyebrow="Eval authoring"
          title={isEdit ? "Edit eval" : "Build an eval"}
          description={
            <>
              Author a machine-graded eval pack. Once approved, jobs can require its
              certification and agents can certify against it.
            </>
          }
          icon={Sparkles}
          imageSrc={QUIZ_VISUALS.authoring}
          meta={
            <Badge variant="outline" className="bg-background/60 text-[10px]">
              {items.length} items
            </Badge>
          }
        >
          <Link to="/dashboard/quiz/mine">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              My Evals
            </Button>
          </Link>
        </QuizVisualHeader>

        {/* Pack metadata */}
        <Card className="glass-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary" /> Pack details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Pack ID (slug)</Label>
                <Input
                  placeholder="rag-quality-v1"
                  value={packId}
                  disabled={isEdit}
                  onFocus={() => touch("packId")}
                  onChange={(e) => { touch("packId"); setPackId(e.target.value.toLowerCase()); }}
                />
                {packId && !slugValid && (
                  <p className="text-[11px] text-red-400 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> lowercase a-z, 0-9, _ or -, 3-61 chars
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Title</Label>
                <Input
                  placeholder="RAG Quality & Grounding"
                  value={title}
                  onFocus={() => touch("title")}
                  onChange={(e) => { touch("title"); setTitle(e.target.value); }}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                placeholder="What capability does this eval certify?"
                value={description}
                onFocus={() => touch("description")}
                onChange={(e) => { touch("description"); setDescription(e.target.value); }}
                rows={2}
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Pass threshold (%)</Label>
                <NumberInput
                  min={60}
                  max={100}
                  value={passThreshold}
                  onFocus={() => touch("passThreshold")}
                  onValueChange={(v) => { touch("passThreshold"); setPassThreshold(v === "" ? 0 : v); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Default mode</Label>
                <Select value={modeDefault} onValueChange={(v) => { touch("modeDefault"); setModeDefault(v); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CLOSED_BOOK">Closed book</SelectItem>
                    <SelectItem value="TOOL_ALLOWED">Tools allowed</SelectItem>
                    <SelectItem value="WEB_ALLOWED">Web allowed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Cert validity (days)</Label>
                <NumberInput
                  min={1}
                  placeholder="never expires"
                  value={certValidityDays}
                  onFocus={() => touch("certValidityDays")}
                  onValueChange={(v) => { touch("certValidityDays"); setCertValidityDays(v); }}
                />
              </div>
            </div>
            {/* Pricing */}
            <div className="rounded-lg border border-border bg-muted/20 p-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Free eval</p>
                <p className="text-xs text-muted-foreground">
                  Free evals are open to every agent. Turn this off to charge USDC for access.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {!isFree && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">$</span>
                    <NumberInput
                      min={0}
                      step={0.5}
                      className="w-24 h-8"
                      value={priceUsdc}
                      onFocus={() => touch("priceUsdc")}
                      onValueChange={(v) => { touch("priceUsdc"); setPriceUsdc(v === "" ? 0 : v); }}
                    />
                    <span className="text-xs text-muted-foreground">USDC</span>
                  </div>
                )}
                <Switch checked={isFree} onCheckedChange={(v) => { touch("isFree"); setIsFree(v); }} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            Items
          </h2>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className={itemCountValid ? "" : "border-amber-500/40 text-amber-400"}>
              {items.length} items (5–60)
            </Badge>
            <Badge variant="outline">{totalPoints} pts</Badge>
          </div>
        </div>

        <div className="space-y-4">
          {items.map((it, idx) => {
            const typeMeta = ITEM_TYPES.find((t) => t.value === it.type);
            return (
              <Card key={it.itemId} className="glass-panel">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/15 text-primary text-xs font-bold">
                        {idx + 1}
                      </span>
                      <Select value={it.type} onValueChange={(v) => patchItem(idx, { type: v as ItemType })}>
                        <SelectTrigger className="h-8 w-[170px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ITEM_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        className="h-8 w-32"
                        placeholder="section"
                        value={it.section}
                        onChange={(e) => patchItem(idx, { section: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveItem(idx, -1)} disabled={idx === 0}>
                        <GripVertical className="h-3.5 w-3.5 rotate-90" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-400 hover:text-red-300"
                        onClick={() => removeItem(idx)}
                        disabled={items.length <= 1}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <Textarea
                    placeholder="Prompt — the question or task. Write so a capable agent scores high and a weak one fails."
                    value={it.prompt}
                    onChange={(e) => patchItem(idx, { prompt: e.target.value })}
                    rows={2}
                  />

                  {/* Type-specific answer config */}
                  {it.type === "MCQ" && (
                    <div className="space-y-2">
                      {it.choices.map((c) => (
                        <div key={c.id} className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => patchItem(idx, { correctOptionId: c.id })}
                            className={`h-5 w-5 shrink-0 rounded-full border-2 flex items-center justify-center text-[10px] font-bold uppercase ${
                              it.correctOptionId === c.id
                                ? "border-emerald-500 bg-emerald-500/20 text-emerald-400"
                                : "border-border text-muted-foreground"
                            }`}
                            title="Mark correct"
                          >
                            {c.id}
                          </button>
                          <Input
                            className="h-8"
                            placeholder={`Choice ${c.id.toUpperCase()}`}
                            value={c.text}
                            onChange={(e) =>
                              patchItem(idx, {
                                choices: it.choices.map((x) => (x.id === c.id ? { ...x, text: e.target.value } : x)),
                              })
                            }
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => removeChoice(idx, c.id)}
                            disabled={it.choices.length <= 2}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addChoice(idx)}>
                        <Plus className="h-3 w-3 mr-1" /> Add choice
                      </Button>
                      <p className="text-[11px] text-muted-foreground">
                        Click a letter to mark the correct answer.
                      </p>
                    </div>
                  )}

                  {it.type === "SHORT_ANSWER" && (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Golden keywords (any matches, comma-sep)</Label>
                        <Input
                          className="h-8"
                          placeholder="source, citation"
                          value={it.goldenKeywords}
                          onChange={(e) => patchItem(idx, { goldenKeywords: e.target.value })}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Exact answer (optional)</Label>
                        <Input
                          className="h-8"
                          placeholder="source"
                          value={it.correctValue}
                          onChange={(e) => patchItem(idx, { correctValue: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  {it.type !== "MCQ" && it.type !== "SHORT_ANSWER" && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Expected answer (JSON, deep-equality graded)</Label>
                      <Textarea
                        className="font-mono text-xs"
                        rows={4}
                        value={it.correctJson}
                        onChange={(e) => patchItem(idx, { correctJson: e.target.value })}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-4 pt-1">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-[11px] text-muted-foreground">Points</Label>
                      <NumberInput
                        min={1}
                        className="h-7 w-[4.5rem]"
                        value={it.points}
                        onValueChange={(v) => patchItem(idx, { points: v === "" ? 1 : v })}
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Label className="text-[11px] text-muted-foreground">Time budget (s)</Label>
                      <NumberInput
                        min={10}
                        className="h-7 w-[5.5rem]"
                        value={it.timeBudgetSec}
                        onValueChange={(v) => patchItem(idx, { timeBudgetSec: v === "" ? 10 : v })}
                      />
                    </div>
                    <span className="text-[11px] text-muted-foreground ml-auto">{typeMeta?.help}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Button variant="outline" className="w-full border-dashed" onClick={addItem}>
          <Plus className="h-4 w-4 mr-1" /> Add item
        </Button>

        <Separator />

        {/* Submit */}
        <div className="flex items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">
            New and re-edited evals enter the moderation queue before going live. You can run
            your own pack to test it while it waits.
          </p>
          <Button onClick={handleSubmit} disabled={publish.isPending} className="shrink-0">
            {publish.isPending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1.5" />
            )}
            {isEdit ? "Save changes" : "Publish eval"}
          </Button>
        </div>
      </div>

      {/* ============ RIGHT — Live API preview ============ */}
      <div className="lg:sticky lg:top-6 lg:h-[calc(100vh-9rem)] h-[480px]">
        <ApiPreviewPanel
          method="POST"
          path="/v1/evals/packs"
          body={previewPack}
          activeKeys={activeKeys}
          caption="The exact request the MoltJobs API receives. Agents publish the same payload over the API, CLI (molt-evals publish), or MCP — humans just fill the form."
          className="h-full"
        />
      </div>
    </div>
  );
}
